import { Board } from 'chessboard/model/board/types';
import { Game } from 'chessboard/model/game/types';
import { StateMachine } from 'chessboard/core/state/config/types';
import { Core } from 'chessboard/core/config/types';
import { isPromotion } from 'chessboard/model/move/is-promotion';
import { ChessError } from 'chessboard/model/error';
import { ErrorCodes } from 'chessboard/model/error/types';
import { shouldDropOrDeselectPiece } from 'chessboard/core/state/model/should-drop-or-deselect-piece';
import { isCastle } from 'chessboard/model/move/is-castle';

export const pieceSelected = {
  anyAPIEvent: ({
    context,
    data: { holdSelectedPiece, move, plyDiff },
    params: { selectedPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    if (
      !holdSelectedPiece &&
      shouldDropOrDeselectPiece({
        api: context.api,
        move,
        pieceSquare: selectedPiece,
        plyDiff,
      })
    ) {
      UpdateParams.resetElevatedState();

      return StateMachine.States.Idle;
    }

    return StateMachine.States.PieceSelected;
  },

  /**
   * The resulting state here depends on the user's set move method.
   *
   * 1. If the move method is 'drag' or 'click' and the user is clicking on the selected
   *    square, we want to pick up the piece, and go to the dragging state.
   * 2. Replace the piece if it's an illegal move - or drag the piece if it's one of our own
   * 3. Open the promotion window if auto-promote is set but the user is holding
   *    the 'alt' key.
   * 4. Make a promotion move if auto-promote is set.
   * 5. Make a normal move if it's legal.
   */
  [Core.UserEvents.PointerdownLeft]: ({
    context,
    data: { coords, keyPressed, square },
    params: { selectedPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const {
      api: { getMove, getPieces, getVariant, move: makeMove },
      mode: { isAllowedToMove },
      options,
      queue,
      renderer,
      state,
    } = context;

    if (!selectedPiece) {
      throw new ChessError({
        code: ErrorCodes.BadData,
        message:
          'Attempted to interact with a selectedPiece but selectedPiece is null or undefined',
      });
    }

    if (square === selectedPiece) {
      const canDrag =
        options.moveMethod === Board.MoveMethods.Drag ||
        options.moveMethod === Board.MoveMethods.Drag_Only ||
        options.moveMethod === Board.MoveMethods.DragOnly;

      // Action 1: Drag the selected piece
      const draggingStartCoords = canDrag
        ? coords
        : renderer?.getCoordsFromSquare(square, options.flipped) || coords;

      UpdateParams.dragPiece(draggingStartCoords, selectedPiece);

      if (options.moveMethod === Board.MoveMethods.Click) {
        state.set({ hideHoverSquare: true });
      }

      renderer?.dragPiece({ coords: draggingStartCoords, square: selectedPiece }, context);

      return StateMachine.States.PieceDragging;
    }

    const moveAttempt = {
      from: selectedPiece,
      to: square,
    };

    const pieces = getPieces();

    const moveOrNull = getMove(moveAttempt);

    const move = isAllowedToMove() && moveOrNull ? { ...moveOrNull, userGenerated: true } : false;

    const isChess960 = getVariant() === Game.Variants.Chess960;
    const rookOnSquare =
      pieces.isDefined(square) && pieces.get(square)?.type === Game.Piece.Types.Rook;
    const rookCastlingDisabled = options.disableRookCastling;
    const stopCastle =
      rookCastlingDisabled && move && !isChess960 && isCastle(move) && rookOnSquare;

    // Action 2: illegal move
    if (!move || stopCastle) {
      UpdateParams.deselectPiece();

      // The square is empty, don't do anything.
      if (!pieces.isDefined(square)) {
        return StateMachine.States.Idle;
      }

      // In Click mode, we just want to select the piece, not pick it up.
      if (options.moveMethod === Board.MoveMethods.Click) {
        const squareCoords = renderer?.getCoordsFromSquare(square, options.flipped) || coords;

        // In Drag mode, select the square and pick up the piece
        UpdateParams.dragPiece(squareCoords, square);

        state.set({ hideHoverSquare: true });

        renderer?.dragPiece({ coords: squareCoords, square }, context);

        return StateMachine.States.PieceDragging;
      }

      // In Drag mode, select the square and pick up the piece
      UpdateParams.dragPiece(coords, square);

      renderer?.dragPiece({ coords, square }, context);

      return StateMachine.States.PieceDragging;
    }

    if (isPromotion(move)) {
      // Action 3: show the promotion area
      if (!options.autoPromote || keyPressed === 'alt') {
        UpdateParams.deselectPiece();
        UpdateParams.dragPiece(<Board.Coords>{}, selectedPiece);
        UpdateParams.showPromotionArea(move);

        renderer?.dragPiece(
          {
            square: selectedPiece,
            toSquare: <Board.SanSquare>move.to,
          },
          context,
        );

        renderer?.openPromotionWindow(move, context);

        return StateMachine.States.PromotionAreaShowing;
      }

      // Action 4: make a promotion move
      UpdateParams.deselectPiece();

      queue.add(makeMove, move, {
        ...move,
        promotion: Game.Piece.Types.Queen,
      });

      return StateMachine.States.Idle;
    }

    // Action 5: make a normal move
    UpdateParams.deselectPiece();

    queue.add(makeMove, move);

    return StateMachine.States.Idle;
  },

  [Core.UserEvents.PointerdownRight]: ({
    context,
    data: { square },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const { options } = context;

    if (!options.enabled || options.disableUserMarkings) {
      return;
    }

    UpdateParams.deselectPiece();
    UpdateParams.setMarkingStartSquare(square);

    return StateMachine.States.AddingMarkings;
  },
};
